import React, { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import queryString from 'query-string'
import InputField from '@components/Shared/InputField/InputField'

import { loginUser, impersonationLogin } from '@actions/auth'
import {
  DEFAULT_ROUTE,
  REGISTER_V2, // TODO: Remove once new sign up flow is complete
} from '@constants/routes'
import {
  selectIsAuthenticated,
  selectIsFetching,
  selectErrorMessage,
  selectStatusCode,
} from '@reducers/auth/authSelectors'

import blueLogo from '@assets/blue-logo.png'
import { Form } from '@ant-design/compatible'
import '@ant-design/compatible/assets/index.css'
/// UI components
import { Button, notification } from 'antd'

/// routing
import { Redirect, Link } from 'react-router-dom'

// Loading Login
import LoadingBlocks from '@components/Shared/Loading/LoadingBlocks'

/// styles
import './Login.scss'

// logo style for IE 10
import { isIE10, logoStyleForIE10 } from '@helpers/crossBrowser'
isIE10 && logoStyleForIE10()

const FormItem = Form.Item

const defaultFields = {
  username: '',
  password: '',
}

const Login = ({ location: { search, state } }) => {
  const dispatch = useDispatch()
  const [shouldRedirect, setShouldRedirect] = useState(false)
  const [fields, setFields] = useState(defaultFields)
  const { token } = queryString.parse(search)

  const isAuthenticated = useSelector(selectIsAuthenticated)
  const isFetching = useSelector(selectIsFetching)
  const errorMessage = useSelector(selectErrorMessage)
  const statusCode = useSelector(selectStatusCode)


  useEffect(() => {
    if (token) {
      dispatch(impersonationLogin(token))
    }
  }, [dispatch, token])

  useEffect(() => {
    if (!isFetching && !isAuthenticated && state) {
      const { success, error } = state || {}
      if (success) {
        notification.success({ message: success })
      }
      if (error) {
        notification.error({ message: error })
      }
    }

    if (isAuthenticated || token) {
      setShouldRedirect(true)
      setFields(defaultFields)
    } else if (!isFetching && errorMessage) {
      if (statusCode === 429) {
        notification.error({
          message: 'Too many login attempts',
          description: 'Please try again later.',
        })
        return
      }

      const splitError = errorMessage.split(',')[0]
      if (splitError === 'Bad Hash') {
        notification.error({
          message: 'Expired Password',
          description: 'Please reset your password',
        })
      } else {
        notification.error({
          message: 'Login Details Incorrect',
          description: 'Your login details were not correct.',
        })
      }
    }
  }, [isAuthenticated, token, isFetching, errorMessage, state])

  const getRedirectObject = () => {
    const { from: { pathname } = {} } = state || {}
    let path = pathname
    const { next } = queryString.parse(search)
    if (next) {
      // Manually split `next` in case it contains a query string.
      const splitRoute = next.split('?')
      let newSearch = splitRoute[1]
      if (newSearch) {
        newSearch = `?${newSearch}`
      }
      path = splitRoute[0]
      return {
        pathname: path,
        search: newSearch,
      }
    }
    if (!path) {
      path = `/${DEFAULT_ROUTE}`
    }

    return {
      pathname: path,
      search,
    }
  }

  const onInputChange = ({ name, value }) => {
    fields[name] = value
    setFields(fields)
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    dispatch(loginUser(fields))
  }

  if (shouldRedirect) {
    return <Redirect to={getRedirectObject()} />
  } else {
    if (isFetching && !isAuthenticated) {
      return (
        <div className="login">
          <LoadingBlocks />
        </div>
      )
    } else {
      if (isAuthenticated) {
        return (
          <div>
            You are already logged in
            <Redirect
              to={{
                ...getRedirectObject(),
                state,
              }}
            />
          </div>
        )
      } else {
        return (
          <div className="login">
            <div className="shadowWrapper">
              <img src={blueLogo} alt={blueLogo} className="login-form-logo" />
              <Form className="login-form" onSubmit={handleSubmit} data-testid="login-form">
                <FormItem>
                  <InputField
                    placeholder="Email"
                    name="username"
                    value={fields.username}
                    icon="user"
                    onChange={onInputChange}
                    className="loginfield"
                    data-testid="login-email-input"
                  />
                </FormItem>
                <FormItem>
                  <InputField
                    placeholder="Password"
                    name="password"
                    value={fields.password}
                    onChange={onInputChange}
                    icon="lock"
                    type="password"
                    className="loginfield"
                    data-testid="login-password-input"
                  />
                </FormItem>
                <FormItem className="altLogin">
                  <Button className="login-button" type="primary" htmlType="submit" data-testid="login-submit-button">
                    Sign in
                  </Button>
                  <Link to="/users/reset-password" data-testid="login-reset-password-link">
                    <Button className="reset-button">Reset Password</Button>
                  </Link>
                </FormItem>
                <div className="loginFormBottom">
                  <p>
                    <span>First time here?&nbsp;</span>
                    <Link to={REGISTER_V2} data-testid="login-create-account-link">Create your account</Link>
                  </p>
                </div>
                <div className="privacy-policy-container">
                  <a
                    className="privacy-policy-text"
                    onClick={() => window.open('https://www.reggora.com/privacy-policy', '_blank')}
                  >
                    Privacy Policy
                  </a>
                </div>
              </Form>
            </div>

            <div className="signin-presentation"></div>
          </div>
        )
      }
    }
  }
}

export default Login
